"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Archive, Download, FolderOpen, Loader2, Folder } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

interface ArchivalProps {
  setActiveSection?: (section: string) => void;
}

export default function Archival({ setActiveSection }: ArchivalProps) {
  const { setIsArchivalMode } = useArchivalMode();
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleOpenArchival = () => {
    setIsArchivalMode(true);
    // Navigate to the main view (chat/engagement overview)
    if (setActiveSection) {
      setActiveSection("chat");
    }
  };

  const handleSelectFolder = async () => {
    try {
      setError(null);
      setSuccess(null);
      if ((window as any).sharePointAPI?.selectFolderDialog) {
        const result = await (window as any).sharePointAPI.selectFolderDialog();
        if (result.success && result.folderPath) {
          setSelectedFolderPath(result.folderPath);
        } else if (!result.canceled) {
          setError(result.error || "Failed to select folder");
        }
      } else {
        setError("SharePoint API not available");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to select folder");
    }
  };

  const handleExtractArchival = async () => {
    if (!selectedFolderPath) {
      setError("Please select a folder first");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if ((window as any).sharePointAPI?.downloadArchival) {
        const result = await (window as any).sharePointAPI.downloadArchival(selectedFolderPath);
        if (result.success) {
          setSuccess(`Archival downloaded successfully! Zip file saved at: ${result.zip_path || selectedFolderPath}`);
          setSelectedFolderPath(""); // Reset after successful download
        } else {
          setError(result.error || "Failed to download archival");
        }
      } else {
        setError("SharePoint API not available");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download archival");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-full items-center justify-center p-8">
      <Card className="w-full max-w-2xl border-white/10 bg-white/5 backdrop-blur-sm">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center gap-3">
            <Archive className="h-6 w-6 text-blue-400" />
            <CardTitle className="text-2xl text-white">Archival</CardTitle>
          </div>
          <p className="mt-2 text-sm text-white/60">
            Access archived data and manage archival operations
          </p>
        </CardHeader>
        <CardContent className="p-8">
          <div className="space-y-4">
            <Button
              onClick={handleOpenArchival}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-16 text-lg font-semibold"
              size="lg"
            >
              <FolderOpen className="mr-3 h-5 w-5" />
              Open Archival
            </Button>
            <p className="text-xs text-white/50 text-center">
              Opens the software in read-only mode with all data from SharePoint
            </p>

            <div className="my-6 border-t border-white/10" />

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">
                  Select Download Location
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={selectedFolderPath}
                    placeholder="No folder selected"
                    readOnly
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                  <Button
                    onClick={handleSelectFolder}
                    disabled={isLoading}
                    className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    variant="outline"
                  >
                    <Folder className="h-4 w-4 mr-2" />
                    Browse
                  </Button>
                </div>
              </div>

              <Button
                onClick={handleExtractArchival}
                disabled={!selectedFolderPath || isLoading}
                className="w-full bg-white/10 hover:bg-white/20 text-white h-16 text-lg font-semibold border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                size="lg"
                variant="outline"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                    Please wait this may take several minutes
                  </>
                ) : (
                  <>
                    <Download className="mr-3 h-5 w-5" />
                    Extract Archival
                  </>
                )}
              </Button>

              {error && (
                <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-3 rounded-md bg-green-500/10 border border-green-500/20">
                  <p className="text-sm text-green-400">{success}</p>
                </div>
              )}

              <p className="text-xs text-white/50 text-center">
                Download archived data to your local machine as a zip file
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

